/**
 * Campos traducibles (ModelDefinition.translatable).
 * Storage: el campo base (`name`) sigue siendo string; cada idioma va en
 * sibling `${key}_${locale}` (p. ej. `name_es`, `name_en`).
 */

import {
  LIST_I18N_LOCALE_CODES,
  normalizeListLocaleCode,
} from "@/utils/listItemI18n";

export { LIST_I18N_LOCALE_CODES, normalizeListLocaleCode };

const TEXT_FIELD_TYPES = ["string", "text", "textarea"];

/**
 * @param {object|null|undefined} field
 * @returns {boolean}
 */
export function isFieldTranslatable(field) {
  if (!field || typeof field !== "object") return false;
  const t = field.translatable;
  if (!(t === true || t === "true" || t === 1 || t === "1")) return false;
  return TEXT_FIELD_TYPES.includes(String(field.type || ""));
}

/**
 * @param {object|null|undefined} field
 * @returns {string[]}
 */
export function getFieldTranslatableLocales(field) {
  if (!isFieldTranslatable(field)) return [];
  const raw = Array.isArray(field.translatableLocales)
    ? field.translatableLocales
    : [];
  return raw
    .map((c) => normalizeListLocaleCode(c))
    .filter(
      (c, i, arr) => LIST_I18N_LOCALE_CODES.includes(c) && arr.indexOf(c) === i
    );
}

/**
 * Clave sibling por idioma: `name` + `es` → `name_es`
 * @param {string} fieldKey
 * @param {string} locale
 * @returns {string}
 */
export function getFieldLocaleSiblingKey(fieldKey, locale) {
  const key = fieldKey != null ? String(fieldKey) : "";
  if (!key) return "";
  return `${key}_${normalizeListLocaleCode(locale)}`;
}

/**
 * Copia siblings i18n (`name_es`, …) de `source` a `target` para un campo traducible.
 * Usado al montar visual groups (`group_general`) en formularios add/edit.
 *
 * @param {object} target
 * @param {object} source
 * @param {object} field
 */
export function copyTranslatableSiblingsInto(target, source, field) {
  if (!target || !source || !field || typeof target !== "object" || typeof source !== "object") {
    return;
  }
  getFieldTranslatableLocales(field).forEach((code) => {
    const sib = getFieldLocaleSiblingKey(field.key, code);
    if (!sib) return;
    if (Object.prototype.hasOwnProperty.call(source, sib)) {
      target[sib] = source[sib];
    }
  });
}

/**
 * Path con sibling: `group.0.title` + `es` → `group.0.title_es`
 * @param {string} valuePath
 * @param {string} locale
 * @returns {string}
 */
export function getLocaleSiblingPath(valuePath, locale) {
  const path = valuePath != null ? String(valuePath) : "";
  if (!path) return getFieldLocaleSiblingKey("", locale);
  return `${path}_${normalizeListLocaleCode(locale)}`;
}

/**
 * Detecta mapa legado `{ en, es }` (ya no se usa para guardar).
 * @param {*} value
 * @returns {boolean}
 */
export function isLegacyLocaleValueMap(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (value.url || value.secure_url || value.path || value._id) return false;
  const keys = Object.keys(value);
  if (!keys.length) return false;
  return keys.every((k) =>
    LIST_I18N_LOCALE_CODES.includes(normalizeListLocaleCode(k))
  );
}

/**
 * Texto del campo base o de un mapa legado (compat).
 * @param {*} value
 * @returns {string}
 */
export function coerceBaseFieldString(value) {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (isLegacyLocaleValueMap(value)) {
    for (const code of LIST_I18N_LOCALE_CODES) {
      if (value[code] != null && String(value[code]).trim() !== "") {
        return String(value[code]);
      }
    }
  }
  return "";
}

/**
 * Resuelve el texto a mostrar desde el documento (siblings + base).
 *
 * @param {object|null|undefined} item
 * @param {string} fieldKey
 * @param {string} [locale]
 * @param {string[]} [preferredLocales]
 * @returns {string}
 */
export function getTranslatedFieldValueFromItem(
  item,
  fieldKey,
  locale,
  preferredLocales = []
) {
  if (!item || typeof item !== "object" || !fieldKey) return "";
  const loc = normalizeListLocaleCode(locale);
  const pickSibling = (code) => {
    const v = item[getFieldLocaleSiblingKey(fieldKey, code)];
    if (v != null && String(v).trim() !== "") return String(v);
    return "";
  };

  const direct = pickSibling(loc);
  if (direct) return direct;

  const preferred = Array.isArray(preferredLocales) ? preferredLocales : [];
  for (const code of preferred) {
    const v = pickSibling(normalizeListLocaleCode(code));
    if (v) return v;
  }
  for (const code of LIST_I18N_LOCALE_CODES) {
    const v = pickSibling(code);
    if (v) return v;
  }

  const base = item[fieldKey];
  if (isLegacyLocaleValueMap(base)) {
    const fromMap =
      (base[loc] != null && String(base[loc]).trim() !== ""
        ? String(base[loc])
        : "") ||
      preferred
        .map((c) => base[normalizeListLocaleCode(c)])
        .find((v) => v != null && String(v).trim() !== "");
    if (fromMap) return String(fromMap);
    for (const code of LIST_I18N_LOCALE_CODES) {
      if (base[code] != null && String(base[code]).trim() !== "") {
        return String(base[code]);
      }
    }
  }
  return coerceBaseFieldString(base);
}

/**
 * @deprecated Usar getTranslatedFieldValueFromItem con el documento.
 * Mantiene compat con llamadas que solo tienen el valor del campo.
 */
export function getTranslatedFieldValue(value, locale, preferredLocales = []) {
  if (isLegacyLocaleValueMap(value)) {
    const loc = normalizeListLocaleCode(locale);
    if (value[loc] != null && String(value[loc]).trim() !== "") {
      return String(value[loc]);
    }
    const preferred = Array.isArray(preferredLocales) ? preferredLocales : [];
    for (const code of preferred) {
      const n = normalizeListLocaleCode(code);
      if (value[n] != null && String(value[n]).trim() !== "") return String(value[n]);
    }
    for (const code of LIST_I18N_LOCALE_CODES) {
      if (value[code] != null && String(value[code]).trim() !== "") {
        return String(value[code]);
      }
    }
    return "";
  }
  return coerceBaseFieldString(value);
}

/** Alias antiguo */
export const isLocaleValueMap = isLegacyLocaleValueMap;
